import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const questions = [
  "Мне трудно принимать решения.",
  "Мне трудно сказать «нет».",
  "Мне трудно принимать комплименты.",
  "Иногда я чувствую что-то вроде скуки или пустоты, если у меня нет проблем, которые нужно решать.",
  "Когда я делаю что-то приятное для себя, я обычно чувствую вину.",
  "Я говорю себе, что все изменится к лучшему, когда окружающие меня люди будут вести себя подругому.",
  "Мне кажется, что в отношениях я всегда есть для других, но другие редко готовы быть для меня.",
  "Иногда я сосредотачиваюсь на одном человеке в ущерб другим отношениям и обязанностям.",
  "Кажется, я вовлекаюсь в отношения, которые болезненны для меня.",
  "Я обычно не показываю другим, какой я на самом деле.",
  "Когда кто-либо расстраивает меня, я долго сдерживаюсь, но в какой-то момент взрываюсь.",
  "Я пойду на все, чтобы избежать открытого конфликта.",
  "Я часто испытываю чувство страха и обреченности.",
  "Я часто ставлю потребности других выше своих собственных.",
];

const answers = [
  { label: "Полностью не согласен", value: 1 },
  { label: "Умеренно не согласен", value: 2 },
  { label: "Немного не согласен", value: 3 },
  { label: "Немного согласен", value: 4 },
  { label: "Умеренно согласен", value: 5 },
  { label: "Полностью согласен", value: 6 },
];

const interpretations = {
  low: {
    range: "14–30 баллов",
    title: "Низкий уровень созависимости",
    description: "Ваши результаты указывают на низкий уровень признаков созависимого поведения. Вы, скорее всего, обладаете хорошо развитым чувством личных границ, умеете разделять свои потребности и чувства от потребностей и ожиданий других людей. Это может означать эмоциональную зрелость, автономность и устойчивость в межличностных отношениях.",
    points: [
      "Вы вряд ли склонны ставить чужие нужды выше своих собственных",
      "Вероятно, умеете говорить «нет», когда это необходимо",
      "Умеете сохранять себя в отношениях, не теряя идентичности"
    ],
    note: "Это здоровый показатель, однако важно помнить, что даже при низком уровне созависимости возможны временные трудности в отношениях, особенно в стрессовых ситуациях.",
    color: "text-green-600"
  },
  moderateLow: {
    range: "31–45 баллов",
    title: "Умеренно низкий уровень созависимости",
    description: "Вы можете обладать отдельными чертами созависимого поведения, но в целом сохраняете личные границы и чувство собственного «я». Возможно, иногда вы склонны заботиться о других в ущерб себе, но это не является доминирующей чертой вашего поведения.",
    points: [
      "Вы умеете заботиться о других, но временами забываете о себе",
      "Возможны трудности в отстаивании личных границ в близких отношениях",
      "Могут возникать чувства вины, если вы ставите себя на первое место"
    ],
    note: "Рекомендуется обратить внимание на повторяющиеся паттерны в отношениях, особенно если вы чувствуете, что часто эмоционально истощаетесь или берёте на себя слишком много.",
    color: "text-yellow-600"
  },
  moderateHigh: {
    range: "46–60 баллов",
    title: "Умеренно высокий уровень созависимости",
    description: "Ваш результат указывает на выраженные признаки созависимого поведения. Возможно, вы часто берёте на себя чрезмерную ответственность за чувства и поступки других людей, стремитесь контролировать ситуацию или удовлетворять чужие потребности в ущерб себе.",
    points: [
      "Вам может быть трудно отказать другим, даже если это причиняет вам дискомфорт",
      "Вы склонны искать одобрения и боитесь быть отвергнутым",
      "В отношениях вы можете терять собственные желания и цели, подстраиваясь под партнёра"
    ],
    note: "Рекомендуется обратить внимание на эмоциональные границы и механизмы самоценности. Возможно, будет полезной работа с психологом или терапевтом для повышения самоосознания и выстраивания здоровых моделей поведения.",
    color: "text-orange-600"
  },
  high: {
    range: "61–84 балла",
    title: "Высокий уровень созависимости",
    description: "Ваши ответы указывают на высокий уровень созависимости. Это означает, что вы, вероятно, сильно зависите от одобрения других, испытываете трудности в выражении своих нужд и эмоций, и часто подстраиваетесь под окружающих, даже в ущерб себе. Возможны эмоциональное истощение, тревожность, чувство вины и потеря контакта с собственными желаниями.",
    points: [
      "Вы можете чувствовать себя ценным только тогда, когда «нужны» другим",
      "Вы склонны к самопожертвованию, даже если это вредит вашему благополучию",
      "Возможны сложности в распознавании и выражении собственных чувств"
    ],
    note: "Такая созависимость может мешать строить здоровые и равноправные отношения. Работа с психотерапевтом может помочь вам укрепить самооценку, научиться ставить границы и восстанавливать личную автономию.",
    color: "text-red-600"
  }
};

function getInterpretation(score: number) {
  if (score >= 14 && score <= 30) return interpretations.low;
  if (score >= 31 && score <= 45) return interpretations.moderateLow;
  if (score >= 46 && score <= 60) return interpretations.moderateHigh;
  return interpretations.high;
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const percent = Math.round((value / max) * 100);
  return (
    <div className="w-full h-3 bg-muted rounded-full overflow-hidden mb-6 mt-2">
      <div
        className="h-full bg-primary transition-all duration-300"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function PsychologyTest() {
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState<(number | null)[]>(Array(questions.length).fill(null));
  const isLast = step === questions.length;
  const score = responses.reduce((acc, v) => acc + (v || 0), 0);
  const interpretation = getInterpretation(score);

  const handleSelect = (val: number) => {
    const next = [...responses];
    next[step] = val;
    setResponses(next);
    setTimeout(() => {
      if (step < questions.length - 1) {
        setStep((s) => s + 1);
      } else {
        setStep(questions.length);
      }
    }, 120);
  };

  return (
    <div className="flex justify-center items-center min-h-screen">
      <Card className="w-full mx-auto shadow-xl border-0 min-h-screen lg:min-h-[80vh] max-w-md flex flex-col">
        <CardContent className="flex flex-col flex-1 pt-8 pb-4 px-6">
          <h2 className="font-bold text-xl text-center mb-2 mt-2 text-primary">Шкала
          созависимости Спенн–Фишер</h2>
          {!isLast ? (
            <>
              <ProgressBar value={step} max={questions.length} />
              <div className="flex-1 flex flex-col justify-center">
                <div className="font-semibold text-base mt-2 mb-2 text-muted-foreground text-center">Вопрос {step + 1} из {questions.length}</div>
                <div className="text-center mb-8 text-lg font-medium min-h-[80px] flex items-center justify-center">{questions[step]}</div>
              </div>
              <div className="flex flex-col gap-2 w-full max-w-xs mx-auto mb-8 mt-auto">
                {answers.map((a) => (
                  <Button
                    key={a.value}
                    variant={responses[step] === a.value ? "default" : "outline"}
                    className="w-full text-base py-6"
                    onClick={() => handleSelect(a.value)}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
              <div className="flex justify-between w-full mt-2">
                <Button
                  variant="ghost"
                  disabled={step === 0}
                  onClick={() => setStep((s) => s - 1)}
                >
                  ← НАЗАД
                </Button>
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={responses[step] == null}
                  variant="secondary"
                >
                  ВПЕРЁД →
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col w-full flex-1 overflow-y-auto">
            <div className="text-center mb-6">
              <div className="text-2xl font-bold mb-2 text-primary">Ваш результат</div>
              <div className="text-3xl font-bold mb-2">{score} баллов из 84</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🔹</span>
                <div>
                  <div className={`font-bold text-lg ${interpretation.color}`}>
                    {interpretation.range}: {interpretation.title}
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Интерпретация:</h4>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {interpretation.description}
                </p>
              </div>
              
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Что это значит для вас:</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  {interpretation.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                <p className="text-sm text-blue-800 italic">
                  {interpretation.note}
                </p>
              </div>
            </div>
            
            <div className="text-center mt-auto">
              <Button 
                onClick={() => { 
                  setStep(0); 
                  setResponses(Array(questions.length).fill(null)); 
                }}
                className="w-full"
              >
                Пройти ещё раз
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PsychologyTest;
